import { keepPreviousData, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { ClientSearchResult } from 'src/models/user/_types';
import { searchClients } from 'src/models/user/user';

interface ClientAutocompleteProps {
  value?: ClientSearchResult | ClientSearchResult[] | null;
  onChange: (client: ClientSearchResult | ClientSearchResult[] | null) => void;
  label?: string;
  autoFocus?: boolean;
  multiple?: boolean;
}

export const ClientAutocomplete: React.FC<ClientAutocompleteProps> = ({
  value,
  onChange,
  label = 'Select Client',
  autoFocus,
  multiple,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<ClientSearchResult[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        const input = containerRef.current?.querySelector(
          '.MuiAutocomplete-input'
        ) as HTMLElement;
        input?.focus();
      }, 100);
    }
  }, [autoFocus]);

  const {
    data: clients,
    refetch: refetchClients,
    isFetching: searchFetching,
  } = useQuery({
    queryKey: ['users', 'search', searchValue],
    queryFn: () => searchClients({ search: searchValue }),
    enabled: false,
    placeholderData: keepPreviousData,
    staleTime: 5000,
  });

  useEffect(() => {
    if (clients) {
      setOptions(clients);
    }
  }, [clients]);

  const debouncedSearch = useCallback(
    (value: string) => {
      debounce(() => {
        if (value.length >= 2) {
          refetchClients();
        }
      }, 300)();
    },
    [refetchClients]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  return (
    <div ref={containerRef}>
      <Autocomplete
        multiple={multiple}
        options={options}
        getOptionLabel={(option: ClientSearchResult) => option.name || ''}
        renderOption={(props, option: ClientSearchResult) => (
          <Box component="li" {...props}>
            <Stack>
              <Typography>{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.email}
              </Typography>
            </Stack>
          </Box>
        )}
        value={value || (multiple ? [] : null)}
        onChange={(_, newValue) => {
          onChange(multiple ? (newValue as ClientSearchResult[]) : newValue);
        }}
        onInputChange={handleInputChange}
        loading={searchFetching}
        filterOptions={(x) => x}
        noOptionsText={
          searchValue ? 'No accounts found' : 'Start typing to find accounts'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searchFetching ? (
                    <CircularProgress color="inherit" size="small" />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </div>
  );
};
